import type { UseMutationResult } from "@tanstack/react-query";
import { useRef, useState } from "react";

import type { CommonModels } from "@/data/common/common.models";
import { MediaModels } from "@/data/media/media.models";
import { MediaQueries } from "@/data/media/media.queries";
import { logger } from "@/util/logger";
import { type ApplicationException, type GeneralErrorCodes, SharedErrorHandler } from "@/util/vendor/error-handling";

type FileUploadError = ApplicationException<GeneralErrorCodes>;
type FileUploadData = { url: string; id: string; fileName: string };
type UseRequestUpload = UseMutationResult<
  CommonModels.MediaUploadInstructions,
  Error,
  { data: CommonModels.MediaUploadFileRequest }
>;

interface IProps {
  onSuccess?: (data: FileUploadData, state: FileUploadProgress) => void;
  onError?: (data: FileUploadData, err: FileUploadError) => void;
  onChange?: (data: FileUploadData, state: FileUploadProgress) => void;
}
export type FileUploadState = "idle" | "uploading" | "error" | "uploaded";
export type FileUploadProgress = {
  state: FileUploadState;
  progress?: number;
  data?: FileUploadData;
  error?: FileUploadError;
};

export const useFileUpload = (requestUpload: UseRequestUpload, { onError, onSuccess, onChange }: IProps = {}) => {
  const useUploadPut = MediaQueries.useUploadPut();
  const useUploadPost = MediaQueries.useUploadPost();

  const [uploadState, setUploadState] = useState<FileUploadProgress>({
    state: "idle",
    progress: 0,
  });

  const abortController = useRef<AbortController | null>(null);

  const cancelUpload = () => {
    if (abortController) {
      abortController.current?.abort();
      abortController.current = null;
    }
  };

  const uploadFile = async (file: File): Promise<string | undefined> => {
    try {
      const presignedUrlData = await requestUpload.mutateAsync({
        data: {
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
        },
      });

      setUploadState({ state: "uploading", progress: 0 });
      onChange?.(
        { url: (presignedUrlData as any)?.url || "", id: (presignedUrlData as any)?.id || "", fileName: file.name },
        { state: "uploading", progress: 0 },
      );
      abortController.current = new AbortController();
      if (!(presignedUrlData as any)?.url) {
        throw new Error("Upload URL is not provided");
      }
      const request: MediaModels.PutUploadRequest = {
        file,
        url: (presignedUrlData as any).url,
        onUploadProgress: ({ loaded, total }) => {
          if (total == null) {
            return;
          }

          const progress = Math.round((loaded / total) * 100);
          setUploadState({ state: "uploading", progress });
          onChange?.(
            { url: (presignedUrlData as any)?.url || "", id: (presignedUrlData as any)?.id || "", fileName: file.name },
            { state: "uploading", progress },
          );
        },
        abortController: abortController.current,
      };

      if ((presignedUrlData as any)?.method === MediaModels.UploadMethod.POST) {
        await useUploadPost.mutateAsync({
          ...request,
          fields: Object.fromEntries((presignedUrlData as any)?.fields ?? []),
        });
      } else {
        await useUploadPut.mutateAsync(request);
      }

      const data = {
        url: (presignedUrlData as any)?.url || "",
        id: (presignedUrlData as any)?.id || "",
        fileName: file.name,
      };

      onSuccess?.(data, { state: "uploaded", data });
      return data.id;
    } catch (err) {
      const error = SharedErrorHandler.getError(err)!;
      logger.error("Error uploading file", error);
      const data = { url: "", id: "", fileName: file.name };

      onError?.(data, error);
      setUploadState({ state: "error", error });
      return undefined;
    } finally {
      abortController.current = null;
    }
  };

  return {
    uploadState,
    uploadFile,
    cancelUpload,
  };
};
